import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en';
import 'dayjs/locale/fi';
import 'dayjs/locale/nb';
import 'dayjs/locale/pl';
import 'dayjs/locale/sv';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Button, Flex, ListTable, Modal, SubPage, Typo, PillTag } from '../../../ui';
import debounce from '../../../helpers/debounce';
import { useNavigate, useParams } from 'react-router-dom';
import { useCourse, useChangeAccessToCourse } from '../hooks';
import { invitesApi } from '../../invites/invitesApi';
import UserModal from '../../users/components/UserModal';
import { useUsersByCourseId } from '../../users/hooks/useUsersState';
import { useUsersActions } from '../../users/hooks/useUsersActions';
import Loading from '../../../ui/Loading';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';
import { useTranslation } from 'react-i18next';
import { useTasks } from '../../tasks/hooks/useTasksState';
import TasksByUser from '../../tasks/components/TasksByUser';
import PageControls from '../../../ui/Page/PageControls';
import PageHeader from '../../../ui/Page/PageHeader';
import { useLocalization } from '../../auth/hooks';

const CourseUsers = ({ modal }) => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const dispatch = useDispatch();
  const changeAccessToCourse = useChangeAccessToCourse();
  const navigate = useNavigate();

  const locale = useLocalization();
  const course = useCourse();

  const { data: tasksData, isError: tasksError } = useTasks({ courseId: course.id }, { skip: !course.id });

  // tasksApi returns an object with { tasks, users, ... }. We can directly use users
  const taskUsers = useMemo(() => tasksData?.users || [], [tasksData]);

  const [searchQuery, setSearchQuery] = useState('');
  const [userToRemove, setUserToRemove] = useState(null);
  const [savedCurrentPage, setSavedCurrentPage] = useState(0);
  const [endDate, setEndDate] = useState(dayjs());

  const { users = [], isLoading } = useUsersByCourseId({ courseId: course.id, token: searchQuery }, { skip: !course.id });
  const { removeUserFromCourse } = useUsersActions();

  const userTasks = useMemo(() => taskUsers.find((obj) => +obj.id === +userId), [userId, taskUsers]);

  const [userToEditExpiration, setUserToEditExpiration] = useState(null);

  useEffect(() => {
    if (locale) {
      dayjs.locale(locale);
    }
  }, [locale]);

  useEffect(() => {
    const user = userToEditExpiration;
    const date = user?.expires === '0000-00-00' ? dayjs() : dayjs(user?.expires);
    setEndDate(date);
  }, [userToEditExpiration]);

  const openUser = useCallback(
    (obj) => {
      navigate(`${obj.id}`);
    },
    [navigate],
  );

  const openUserTasks = useCallback(
    (user) => {
      navigate(`${user.id}/tasks`);
    },
    [navigate],
  );

  const onCloseUserTasks = () => {
    navigate(`/courses/${course.id}/users`);
  };

  const removeFromCourse = useCallback(async () => {
    await removeUserFromCourse({ courseId: course.id, userId: userToRemove.id });
    setUserToRemove(null);
  }, [removeUserFromCourse, course, userToRemove]);

  const isNeverExpire = (date) => date === '2099-12-31' || date === '0000-00-00';

  const saveAccessPeriodEndDate = async () => {
    await changeAccessToCourse({ id: course.id, expires: endDate.toISOString().split('T')[0], userId: userToEditExpiration.id });
    setUserToEditExpiration(null);
  };

  const tableInfo = [
    {
      label: t('globals.name'),
      data: (user) => (
        <>
          <Typo.Text>{user.name}</Typo.Text>
          <Typo.Info fontWeight='300'>{user.id}</Typo.Info>
        </>
      ),
    },
    {
      label: t('globals.tasks'),
      data: (user) => {
        const userTasks = taskUsers.find((obj) => obj.id === user.id);
        if (userTasks) {
          let resultsUncorrected = 0;
          let wrongResults = 0;
          Object.values(userTasks?.tasks).forEach((task) => {
            task.hasBeenApproved === 99 && resultsUncorrected++;
            task.hasBeenApproved === 0 && wrongResults++;
          });
          const iconLabel = resultsUncorrected > 0 ? t('course.awaitsFeedback') : wrongResults > 0 ? t('course.wrongAnswers') : t('course.allCorrect');
          return (
            <Flex width='100%' justify='center'>
              <PillTag
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openUserTasks(user);
                }}
                label={`${!!resultsUncorrected > 0 ? resultsUncorrected : !!wrongResults > 0 ? wrongResults : ''} ${iconLabel}`}
              />
            </Flex>
          );
        } else {
          return (
            <Flex width='100%' justify='center'>
              <Typo.MediumText>-</Typo.MediumText>
            </Flex>
          );
        }
      },
    },
    { label: t('course.accessUntil'), data: (user) => (isNeverExpire(user.expires) ? t('globals.forever') : user.expires) },
    // { label: 'Is Manager' },
    // { label: t('globals.tasks') },
    // { label: t('globals.quizes') },
    // { label: 'Pages viewed' },
    // { label: 'Groups' },
    // { label: 'Progress' },
    {
      label: '',
      menuOptions: (user) => {
        const userTasks = taskUsers.find((obj) => obj.id === user.id);
        const arr = [
          { label: t('course.userDetails'), onClick: openUser },
          ...(userTasks ? [{ label: t('course.openUserTasks'), onClick: openUserTasks }] : []),
          { label: t('course.editAccessExpire'), onClick: setUserToEditExpiration },
          { label: t('course.removeUser'), onClick: setUserToRemove, variant: 'action' },
        ];
        return arr;
      },
    },
  ].filter(Boolean);

  const handleSearch = debounce(
    (value) => {
      setSearchQuery(value);
    },
    [setSearchQuery],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const openInviteDialog = () => {
    dispatch(
      invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
        if (draft) {
          draft.isInviteModalOpen = true;
          draft.modalProps = { course };
        }
      }),
    );
  };

  return (
    <SubPage
      staticElements={
        <PageHeader>
          <PageControls
            onClearSearch={clearSearch}
            onSearch={handleSearch}
            placeholder={t('course.searchUser')}
            disableClear={!searchQuery}
            openModelButton={
              <Button icon='Invite' onClick={openInviteDialog}>
                {t('invites.inviteUser')}
              </Button>
            }
          />
        </PageHeader>
      }
    >
      {isLoading ? (
        <Loading />
      ) : (
        <ListTable
          tableInfo={tableInfo}
          data={users}
          tableName={'courseUsers'}
          onRowClick={openUser}
          keepTrackOfPage={setSavedCurrentPage}
          defaultStartPage={savedCurrentPage}
        />
      )}

      <UserModal open={!!userId && !modal} userId={userId} />
      <TasksByUser open={!!modal} userTasks={userTasks} handleClose={onCloseUserTasks} courseId={course.id} />
      {tasksError && !isLoading && (
        <Flex width='100%'>
          <Typo.Info>{t('course.taskInfoUnavailable')}</Typo.Info>
        </Flex>
      )}
      <Modal
        titleHasLine
        open={!!userToEditExpiration}
        onClose={() => setUserToEditExpiration(null)}
        showActions={true}
        onAccept={saveAccessPeriodEndDate}
        onCancel={() => setUserToEditExpiration(null)}
        titleChildren={
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader>{t('course.editAccessExpire')}</Typo.ContentSmallHeader>
            <Typo.Text>{userToEditExpiration?.name}</Typo.Text>
          </Flex>
        }
      >
        {userToEditExpiration?.expires === '0000-00-00' ? (
          <Typo.Text>{t('course.cantEditForever')}</Typo.Text>
        ) : (
          <Flex direction='column' gap='10' align='flex-start'>
            <Typo.Text>{t('course.editExpireDate')}</Typo.Text>
            <Flex>
              {locale && (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                  <DatePicker
                    format='LL'
                    label={t('globals.endDate')}
                    value={dayjs(endDate)}
                    onChange={(newValue) => {
                      const dateObj = new Date(newValue);
                      const dateFormat = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60 * 1000);
                      setEndDate(dateFormat);
                    }}
                    slotProps={{
                      popper: { sx: { zIndex: 10000 } },
                    }}
                  />
                </LocalizationProvider>
              )}
            </Flex>
          </Flex>
        )}
      </Modal>
      <ConfirmDelete
        onClose={setUserToRemove}
        onAcceptDelete={removeFromCourse}
        open={!!userToRemove}
        title={t('course.confirmRemoval')}
        description={t('course.confirmRemovalDescription', { userToRemove: userToRemove?.name, course: course?.name })}
      />
    </SubPage>
  );
};

export default CourseUsers;
